import Decimal from 'decimal.js-light';
import {
    SuperBonusEligibility,
    BonusPercentage,
} from '@superbet-group/betting.lib.payments';
import { FunctionsObject } from '@superbet-group/web.lib.utilities';
import { BetSlipPurchaseType } from '@superbet-group/web.types';

import {
    ComputedGetters,
    Getter,
} from '@app/store/StoreUtil';
import System from '@core/models/betSlip/System';
import BonusEligibility from '@core/models/bonus/BonusEligibility';
import { BetSlipType } from '@core/models/shared/betSlip/betSlipEnums';
import { calculateStakePerCombination } from '@core/services/betSlip/CalculationHelper';
import { PayoutService } from '@core/services/betSlip/PayoutService';
import { TaxService } from '@core/services/betSlip/TaxService';
import { getEventOddMap } from '@store/modules/ui/betSlip/helpers/odds';
import { getNonFixedSelectionsCount } from '@store/modules/ui/betSlip/helpers/systems';

import { BetSlipState } from './types';
import SelectionChange from '@core/models/betSlip/SelectionChange';
import { getAvailableBetslipPurchaseTypes } from '@src/app/utils/config/sportConfig';

type BetSlipGetter<Type, Getters extends FunctionsObject> =
    Getter<BetSlipState, ComputedGetters<Getters>, Type>;

export interface Getters {
    selections: BetSlipGetter<BetSlipState['selections'], Getters>;
    totalCoefficient: BetSlipGetter<number, Getters>;
    selectionsWithOdd: BetSlipGetter<BetSlipState['selections'], Getters>;
    selectedSystems: BetSlipGetter<System[], Getters>;
    autoUpdateChanges: BetSlipGetter<BetSlipState['autoUpdateChanges'], Getters>;
    changes: BetSlipGetter<SelectionChange[], Getters>;
    errors: BetSlipGetter<BetSlipState['errors'], Getters>;
    betSlipErrors: BetSlipGetter<BetSlipState['betSlipErrors']['sportValidation'], Getters>;
    rawStake: BetSlipGetter<BetSlipState['stake'], Getters>;
    stake: BetSlipGetter<{ net: number; gross: number; stakePerCombination: number }, Getters>;
    type: BetSlipGetter<BetSlipState['type'], Getters>;
    systems: BetSlipGetter<System[], Getters>;
    payout: BetSlipGetter<BetSlipState['payout'], Getters>;
    activeBonusEligibility: BetSlipGetter<BonusEligibility, Getters>;
    // derived
    eventOddMap: BetSlipGetter<Record<number, Set<string>>, Getters>;
    // can be moved to components
    isOnline: BetSlipGetter<boolean, Getters>;
    isEmpty: BetSlipGetter<boolean, Getters>;
    isSimple: BetSlipGetter<boolean, Getters>;
    hasBonusInfo: BetSlipGetter<boolean, Getters>;
    isCodeShown: BetSlipGetter<boolean, Getters>;
    autoApplyChanges: BetSlipGetter<boolean, Getters>;
    warning: BetSlipGetter<BetSlipState['warning'], Getters>;
    selectionsCount: BetSlipGetter<number, Getters>;
    // superBonus
    userBalance: BetSlipGetter<number, Getters>;
    sportBonusBalance: BetSlipGetter<number, Getters>;
    isSuperBonusEnabled: BetSlipGetter<boolean, Getters>;
    superBonus: BetSlipGetter<SuperBonusEligibility, Getters>;
    superBonusPercentage: BetSlipGetter<BonusPercentage, Getters>;
    superBonusSelectionCoefficientMinimum: BetSlipGetter<number, Getters>;
    superBonusNumberOfSelectionsMinimum: BetSlipGetter<number, Getters>;
    bonusError: BetSlipGetter<BetSlipState['bonusError'], Getters>;
    bonusWarning: BetSlipGetter<BetSlipState['bonusWarning'], Getters>;
    isBonusSelected: BetSlipGetter<boolean, Getters>;
    hasOngoingEvents: BetSlipGetter<boolean, Getters>;
    hasPrematchEvents: BetSlipGetter<boolean, Getters>;
    isRecreatingTicket: BetSlipGetter<boolean, Getters>;
    isSubmittingOdd: BetSlipGetter<Record<string, boolean>, Getters>;
    [key: string]: BetSlipGetter<any, Getters>; // to satisfy vuex typings
}

export type BetSlipGetters = ComputedGetters<Getters>;

export function createGetters({
    calculateNetStake,
}: {
    calculateNetStake: (stake: number, purchaseType: BetSlipPurchaseType) => number;
}): Getters {
    const getters: Getters = {
        selections(state) {
            return state.selections;
        },
        totalCoefficient(state, getters) {
            return getters.selectionsWithOdd.reduce(
                (acc, curr) => acc.times(curr.getOddValue()!),
                new Decimal(1),
            ).toNumber();
        },
        selectedSystems(state, getters) {
            return getters.systems.filter((s) => s.isSelected);
        },
        systems(state, getters) {
            const selectedSystemNumbers = state.selectedSystems;
            const nonFixedSelectionsCount = getNonFixedSelectionsCount(getters.selections);
            const systems: System[] = [];

            for (let i = 1; i <= nonFixedSelectionsCount; i++) {
                systems.push(new System({
                    minNumber: i,
                    fromNumber: nonFixedSelectionsCount,
                    isSelected: selectedSystemNumbers.includes(i),
                }));
            }

            return systems;
        },
        rawStake(state) {
            return state.stake;
        },
        type(state) {
            return state.type;
        },
        autoUpdateChanges(state) {
            return state.autoUpdateChanges;
        },
        changes(state) {
            return Object.values(state.changes);
        },
        errors(state) {
            return state.errors;
        },
        betSlipErrors(state) {
            return state.betSlipErrors.sportValidation.slice(0, 1);
        },
        isOnline(state) {
            return state.purchaseType === BetSlipPurchaseType.online;
        },
        isEmpty(state, getters) {
            return getters.selections.length === 0;
        },
        isSimple(state, getters) {
            return getters.type === BetSlipType.simple;
        },
        selectionsWithOdd(state, getters) {
            return getters.selections.filter((s) => s.odd);
        },
        userId(state, getters, rootState: any, rootGetters: any) {
            return rootGetters['data/authentication/userId'];
        },
        userBalance(state, getters, rootState: any, rootGetters: any) {
            return rootGetters['data/authentication/userBalance'];
        },
        sportBonusBalance(state, getters, rootState: any, rootGetters: any) {
            return rootGetters['data/authentication/sportBonusBalance'];
        },
        activeBonusEligibility(state, getters, rootState: any): BonusEligibility {
            return rootState.ui?.user?.bonuses?.activeBonusEligibility;
        },
        isSuperBonusEnabled() {
            return PayoutService.getInstance().isSuperBonusEnabled;
        },
        superBonusSelectionCoefficientMinimum() {
            return PayoutService.getInstance().superBonusMinCoefficient;
        },
        superBonusNumberOfSelectionsMinimum() {
            return PayoutService.getInstance().superBonusNumberOfSelectionsMinimum;
        },
        superBonus(state, getters) {
            return PayoutService.getInstance().getSuperBonusEligibility({
                stake: getters.rawStake || 0,
                purchaseType: state.purchaseType,
                monetaryInfo: {
                    userBalance: getters.userBalance,
                    sportBonusBalance: getters.sportBonusBalance,
                },
                selections: getters.selectionsWithOdd,
            });
        },
        superBonusPercentage(state, getters) {
            return PayoutService.getInstance().getSuperBonus(
                getters.selectionsWithOdd,
                state.purchaseType,
            );
        },
        eventOddMap(state, getters) {
            return getEventOddMap(getters.selections);
        },
        hasBonusInfo(state, getters): boolean {
            return !getters.isOnline;
        },
        isCodeShown(state, getters): boolean {
            return BetSlipPurchaseType.offline === state.purchaseType
                || (getAvailableBetslipPurchaseTypes().length > 1 && getters.userId == null);
        },
        stake(state, getters) {
            const grossStake = getters.rawStake;
            if (!grossStake) {
                return {
                    net: 0,
                    gross: 0,
                    stakePerCombination: 0,
                };
            }
            const netStake = calculateNetStake(grossStake, state.purchaseType);
            const stakePerCombination = calculateStakePerCombination(grossStake, getters.totalNumberOfSelectedCombinations);
            return {
                gross: grossStake,
                net: netStake,
                stakePerCombination,
            };
        },
        taxPercentage(state) {
            return TaxService.getInstance().getStakeTaxByPurchaseType(state.purchaseType);
        },
        payout(state) {
            return state.payout;
        },
        autoApplyChanges(state, getters) {
            return state.autoUpdateChanges || !getters.isOnline;
        },
        warning(state) {
            return state.warning;
        },
        selectionErrors(state) {
            return state.selectionErrors;
        },
        selectionAddError(state) {
            return state.selectionAddError;
        },
        totalNumberOfCombinations(state, getters) {
            return getters.systems.reduce((acc, curr) => acc + curr.numberOfCombinations, 0);
        },
        totalNumberOfSelectedCombinations(state, getters) {
            return getters.selectedSystems.reduce((acc, curr) => acc + curr.numberOfCombinations, 0);
        },
        selectionsCount(state, getters) {
            return getters.selections.length;
        },
        fixedSelectionsCount(state, getters) {
            return getters.selections.filter((s) => s.isFixed).length;
        },
        bonusError(state) {
            return state.bonusError;
        },
        bonusWarning(state) {
            return state.bonusWarning;
        },
        isBonusSelected(state) {
            return state.isBonusSelected;
        },
        hasOngoingEvents(state, getters) {
            return getters.selections.some((selection) => selection.event.isOngoing());
        },
        hasPrematchEvents(state, getters) {
            return getters.selections.some((selection) => !selection.event.isOngoing());
        },
        isRecreatingTicket(state) {
            return state.isRecreatingTicket;
        },
        isSubmittingOdd(state) {
            return state.isSubmittingOdd;
        },
    };
    return getters;
}
